import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  JOB_STATUS_LABEL,
  createJob,
  getOrder,
  listAssets,
  listJobs,
  uploadSource,
  type Job,
  type Order,
} from '../lib/api';

export default function Studio() {
  const { code = '' } = useParams();
  const [order, setOrder] = useState<Order | null>(null);
  const [error, setError] = useState('');
  const [sourcePreview, setSourcePreview] = useState('');
  const [outputPreview, setOutputPreview] = useState('');
  const [color, setColor] = useState('墨色');
  const [background, setBackground] = useState('素净');
  const [attire, setAttire] = useState('正装');
  const [job, setJob] = useState<Job | null>(null);
  const [busyUpload, setBusyUpload] = useState(false);
  const [busyJob, setBusyJob] = useState(false);
  const pollRef = useRef<number | null>(null);

  const refreshOrder = useCallback(async () => {
    const o = await getOrder(code);
    setOrder(o);
    return o;
  }, [code]);

  useEffect(() => {
    refreshOrder().catch(() => setError('订单加载失败'));
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [refreshOrder]);

  async function loadLatestOutput() {
    const { assets } = await listAssets(code);
    const output = assets.find((a) => a.kind === 'output');
    if (output) setOutputPreview(output.preview_url);
    const source = assets.find((a) => a.kind === 'source');
    if (source && !sourcePreview) setSourcePreview(source.preview_url);
  }

  function startPolling(jobId: string) {
    if (pollRef.current) window.clearInterval(pollRef.current);
    pollRef.current = window.setInterval(async () => {
      try {
        const { jobs } = await listJobs(code);
        const row = jobs.find((j) => j.id === jobId) ?? jobs[0] ?? null;
        if (!row) return;
        setJob(row);
        if (row.status === 'succeeded') {
          if (pollRef.current) window.clearInterval(pollRef.current);
          pollRef.current = null;
          await loadLatestOutput();
          setBusyJob(false);
        } else if (row.status === 'failed') {
          if (pollRef.current) window.clearInterval(pollRef.current);
          pollRef.current = null;
          setBusyJob(false);
        }
      } catch {
        /* keep polling briefly */
      }
    }, 1500);
  }

  async function onUpload(file: File | null) {
    if (!file) return;
    setBusyUpload(true);
    setError('');
    try {
      const res = await uploadSource(code, file);
      setSourcePreview(res.preview_url);
    } catch (err) {
      setError(err instanceof Error ? err.message : '上传失败');
    } finally {
      setBusyUpload(false);
    }
  }

  async function onStartJob() {
    setBusyJob(true);
    setError('');
    setOutputPreview('');
    try {
      const created = await createJob(code, {
        style: { color, background, attire },
      });
      setJob(created);
      startPolling(created.id);
    } catch (err) {
      setBusyJob(false);
      setError(err instanceof Error ? err.message : '创建任务失败');
    }
  }

  if (!order && !error) {
    return <p className="type-body-md text-ink-secondary">加载中…</p>;
  }

  if (order && order.status !== 'paid') {
    return (
      <section className="work-card waiting">
        <h2 className="type-display-md text-ink">创作室暂未开放</h2>
        <p className="type-body-md text-ink">
          当前订单状态为「
          {order.status === 'pending_payment'
            ? '待支付'
            : order.status === 'pending_confirm'
              ? '待确认'
              : order.status === 'rejected'
                ? '已拒绝'
                : order.status}
          」。请先完成闲鱼拍下与管理员确认。
        </p>
        <div className="row" style={{ justifyContent: 'center' }}>
          {order.status === 'pending_payment' ? (
            <Link className="btn-primary" to={`/order/${code}/pay`}>
              去支付
            </Link>
          ) : null}
          {order.status === 'pending_confirm' ? (
            <Link className="btn-primary" to={`/order/${code}`}>
              查看订单
            </Link>
          ) : null}
          <Link className="btn-secondary" to={`/order/${code}`}>
            订单详情
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="stack">
      <div className="work-card">
        <h2 className="type-display-md text-ink">创作室</h2>
        <p className="type-body-md text-ink-secondary">
          订单 <span className="mono">{code}</span> 已支付。请上传原图，选择简单样式后开始生成。
        </p>
      </div>

      <div className="work-card stack">
        <h2 className="type-display-md text-ink">上传原图</h2>
        <label className="field">
          选择图片（JPEG / PNG / WebP，最大 8MB）
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            disabled={busyUpload}
            onChange={(e) => onUpload(e.target.files?.[0] ?? null)}
          />
        </label>
        {sourcePreview ? (
          <div>
            <p className="type-body-md text-ink-secondary">原图预览</p>
            <img className="preview" src={sourcePreview} alt="原图预览" />
          </div>
        ) : null}
      </div>

      <div className="work-card stack">
        <h2 className="type-display-md text-ink">样式（将作为不透明 JSON 提交）</h2>
        <label className="field">
          色调
          <select value={color} onChange={(e) => setColor(e.target.value)}>
            <option value="墨色">墨色</option>
            <option value="暖棕">暖棕</option>
            <option value="青灰">青灰</option>
          </select>
        </label>
        <label className="field">
          背景
          <select value={background} onChange={(e) => setBackground(e.target.value)}>
            <option value="素净">素净</option>
            <option value="淡雅纹理">淡雅纹理</option>
            <option value="书房">书房</option>
          </select>
        </label>
        <label className="field">
          着装
          <select value={attire} onChange={(e) => setAttire(e.target.value)}>
            <option value="正装">正装</option>
            <option value="中式">中式</option>
            <option value="保持原貌">保持原貌</option>
          </select>
        </label>
        <div className="row">
          <button
            type="button"
            className="btn-primary"
            disabled={busyJob || !sourcePreview}
            onClick={onStartJob}
          >
            {busyJob ? '生成中…' : '开始生成'}
          </button>
        </div>
      </div>

      {error ? <div className="error">{error}</div> : null}

      {job ? (
        <div className="work-card stack">
          <h2 className="type-display-md text-ink">生成任务</h2>
          <p className="type-body-md text-ink">
            状态：
            <span className={`status-pill ${job.status}`}>
              {JOB_STATUS_LABEL[job.status] ?? job.status}
            </span>
          </p>
          {job.error ? <div className="error">{job.error}</div> : null}
          {outputPreview ? (
            <div>
              <p className="type-body-md text-ink-secondary">成品预览（经资产代理）</p>
              <img className="preview" src={outputPreview} alt="成品预览" />
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="row">
        <Link className="btn-secondary" to={`/order/${code}`}>
          返回订单
        </Link>
      </div>
    </section>
  );
}
